import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; harm: readonly number[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', stab: ['G3', 'C4', 'E4', 'G4'], harm: [0, 4, 7] },
  C7: { root: 'C2', stab: ['G3', 'C4', 'E4', 'Bb4'], harm: [0, 4, 7, 10] },
  F: { root: 'F2', stab: ['A3', 'C4', 'F4', 'A4'], harm: [5, 9, 0] },
  G7: { root: 'G2', stab: ['G3', 'B3', 'D4', 'F4'], harm: [7, 11, 2, 5] },
  Am: { root: 'A2', stab: ['A3', 'C4', 'E4', 'A4'], harm: [9, 0, 4] },
  Dm: { root: 'D2', stab: ['A3', 'D4', 'F4', 'A4'], harm: [2, 5, 9] },
};

const PROGS = [
  ['C', 'G7', 'G7', 'C'],
  ['C', 'F', 'G7', 'C'],
  ['C', 'Am', 'Dm', 'G7'],
  ['F', 'C', 'G7', 'C'],
  ['C', 'C7', 'F', 'G7'],
] as const;

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 2], [4, 'C6', 3], [7, 'B5', 1, 0.7], [8, 'C6', 2], [10, 'G5', 3], [13, 'E5', 1, 0.7], [14, 'F5', 2]],
  [[0, 'G5', 2], [2, 'F5', 1, 0.7], [3, 'D5', 3], [6, 'B4', 2], [8, 'D5', 2], [10, 'F5', 3], [13, 'A5', 1, 0.7], [14, 'G5', 2]],
  [[0, 'B5', 3], [3, 'A5', 1, 0.7], [4, 'G5', 2], [6, 'F5', 2], [8, 'D5', 3], [11, 'F5', 1, 0.7], [12, 'G5', 2], [14, 'B5', 2]],
  [[0, 'C6', 3], [3, 'G5', 3], [6, 'E5', 2], [8, 'C5', 6], [14, 'G4', 1, 0.7], [15, 'B4', 1, 0.8]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 2.8], [3, 7, 0.9], [6, 12, 1.8], [8, 0, 2.8], [11, 7, 0.9]],
  [[0, 0, 2.6], [3, 0, 0.9], [6, 7, 1.8], [8, 7, 1.8], [10, 0, 0.9], [11, 4, 0.9]],
  [[0, 0, 2.8], [3, 12, 0.9], [4, 7, 1.8], [8, 0, 2.8], [11, 4, 0.9], [12, 7, 1.8]],
];

const BANJOS: readonly (readonly [step: number, up: number, vel: number][])[] = [
  [[0, 0, 0.85], [2, 1, 0.5], [3, 0, 0.62], [6, 0, 0.78], [8, 0, 0.82], [10, 1, 0.5], [11, 0, 0.62], [14, 0, 0.78]],
  [[2, 0, 0.8], [3, 1, 0.5], [6, 0, 0.75], [7, 1, 0.45], [10, 0, 0.8], [11, 1, 0.5], [14, 0, 0.75], [15, 1, 0.45]],
  [[0, 0, 0.82], [3, 0, 0.6], [4, 1, 0.45], [6, 0, 0.75], [8, 0, 0.8], [11, 0, 0.6], [12, 1, 0.45], [14, 0, 0.75]],
];

const KEYS = [0, 2, -3, -2, -5, 3, 5] as const;
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;
const CURIMBOS = ['X..x..x.X..x..x.', 'X..x...xX..x..x.', 'X.xx..x.X..x..xo'] as const;
const FILLS = ['X..x..x.X.x.xxxx', 'X..x..x.Xx.xx.x.', 'X..x..x.X.xxX.xx'] as const;
const TOQUES = ['..o..oo...o..o.o', '.o.o..o..o.o..o.', '..o.o.o...o.o.oo'] as const;
const MARACAS = ['Xo-oxo-oXo-oxo-o', 'Xoxoxo-oXoxoxo-o'] as const;
const RECO = '..x...x...x..xx.';
const TAPA = '....X.......X...';
const ROLL = 'x.x.x.x.xxxxxxxx';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function under(note: string, harm: readonly number[]): string {
  const m = midi(note);
  for (let d = 3; d <= 9; d++) if (harm.includes((((m - d) % 12) + 12) % 12)) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(CARIMBO, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const curP = ch.pick(CURIMBOS);
  const fillP = ch.pick(FILLS);
  const toqueP = ch.pick(TOQUES);
  const maracaP = ch.pick(MARACAS);
  const bassRow = ch.pick(BASSES);
  const banjoRow = ch.pick(BANJOS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        MAJOR,
        [72, 86],
        79,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 33) m += 12;
    while (m > 44) m -= 12;
    return noteName(m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 58, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const groove1 = find(form, 'A');
  const rng = createRng(`carimbo-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const strum = (bar: number, step: number, notes: readonly string[], up: boolean, vel: number) =>
    chord('banjo', bar, step, up ? [...notes].reverse() : notes, s * 1.6, vel, up ? 0.009 : 0.013);
  const maracas = (bar: number, scale: number) => {
    for (const h of hits(maracaP)) hit(h.step % 2 === 0 ? 'maracaL' : 'maracaR', bar, h.step, human(h.vel * scale));
  };

  const autos: Score['autos'] = [
    { v: 'banjoLp', points: [[g.at(0), 1500], [g.at(intro), 5200]] },
    { v: 'banjoLp', points: [[g.at(total - 1), 5200], [g.at(total), 1800]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? CHORDS.C! : prog[i]!;
    const next = prog[cycle(b.bar - intro + 1, 4)]!;
    const drop = b.name === 'drop' || b.name === 'drop2';
    const band = drop || b.name === 'A' || (b.name === 'outro' && !end);
    const stab = c.stab.map(tr);

    if (end) {
      hit('curimbo', b.bar, 0, 1);
      hit('tapa', b.bar, 0, 0.7);
      hit('crash', b.bar, 0, 0.8);
      hit('impact', b.bar, 0, 0.8);
      strum(b.bar, 0, stab, false, 0.95);
      play('bass', b.bar, 0, low(c.root), g.barLength * 0.6, 0.9);
      play('sub', b.bar, 0, low(c.root), g.barLength * 0.8, 0.9);
      play('sax', b.bar, 0, tr('C5'), g.barLength * 0.85, 0.8);
      continue;
    }

    if (band) {
      const pattern = b.index % 4 === 3 ? fillP : curP;
      for (const h of hits(pattern)) hit('curimbo', b.bar, h.step, human(h.vel));
      for (const h of hits(TAPA)) hit('tapa', b.bar, h.step, human(drop ? 1 : 0.92));
      if (!(b.name === 'A' && b.last)) for (const h of hits(toqueP)) hit('toque', b.bar, h.step, human(h.vel * (drop ? 1 : 0.85)));
      maracas(b.bar, drop ? 1 : 0.85);
      if (drop && e >= 0.6) for (const h of hits(RECO)) hit('reco', b.bar, h.step, human(h.vel));
      const root = midi(low(c.root));
      for (const [step, semis, len] of bassRow) {
        const note = noteName(root + semis);
        play('bass', b.bar, step, note, len * s * 0.95, human(0.9));
        play('sub', b.bar, step, note, len * s * 0.95, 0.85);
      }
      const ahead = low(next.root);
      play('bass', b.bar, 14, ahead, s * 1.8, human(0.85));
      play('sub', b.bar, 14, ahead, s * 1.8, 0.8);
      for (const [step, up, vel] of banjoRow) strum(b.bar, step, stab, up === 1, human(vel * (drop ? 1 : 0.88)));
    } else if (b.name === 'break') {
      const pattern = b.last ? fillP : curP;
      for (const h of hits(pattern)) hit('curimbo', b.bar, h.step, human(h.vel * 0.95));
      for (const h of hits(TAPA)) hit('tapa', b.bar, h.step, human(0.85));
      if (!b.last) for (const h of hits(toqueP)) hit('toque', b.bar, h.step, human(h.vel));
      maracas(b.bar, 0.7);
      play('sub', b.bar, 0, low(c.root), s * 14, 0.65);
      for (const st of [0, 8]) strum(b.bar, st, stab, false, human(0.55));
    } else if (b.name === 'intro') {
      for (const st of [0, 6, 8, 14]) strum(b.bar, st, stab, false, human(st % 8 === 0 ? 0.6 : 0.45));
      if (b.index >= b.bars / 2) maracas(b.bar, 0.6);
      if (b.last) for (const h of hits('........x..x.xx.')) hit('curimbo', b.bar, h.step, 0.78);
    }

    const row = hook[i]!;
    const flute = (vel: number) => {
      for (const [step, note, len, v] of row) play('flute', b.bar, step, tr(note), len * s * 0.9, human((v ?? 0.95) * vel));
    };
    const sax = (vel: number, harm: number, octave: number) => {
      for (const [step, note, len, v] of row) {
        const n = transpose(tr(note), -12);
        play('sax', b.bar, step, n, len * s * 0.92, human((v ?? 0.95) * vel));
        if (harm > 0) play('saxHarm', b.bar, step, under(n, c.harm.map((x) => (x + key + 120) % 12)), len * s * 0.9, (v ?? 0.9) * harm);
        if (octave > 0) play('flute', b.bar, step, tr(note), len * s * 0.88, (v ?? 0.9) * octave);
      }
    };
    if (b.name === 'A') flute(0.9);
    if (b.name === 'intro' && b.index >= b.bars / 2) flute(0.6);
    if (b.name === 'drop') sax(1, e >= 0.85 ? 0.55 : 0, e >= 0.7 ? 0.45 : 0);
    if (b.name === 'drop2') sax(1, e >= 0.6 ? 0.55 : 0, 0.5);
    if (b.name === 'break' && b.index < b.bars / 2) flute(0.75);

    if (b.name === 'A' && b.last) {
      for (const h of hits('........x.x.xxxx')) hit('toque', b.bar, h.step, 0.4 + (0.55 * h.step) / 16);
    }
    if (b.name === 'break' && b.last) {
      for (const h of hits(ROLL)) hit('toque', b.bar, h.step, 0.35 + (0.6 * h.step) / 16);
    }
  }

  events.push({ v: 'rise', t: g.at(Math.max(intro, drop1 - 2)), end: g.at(drop1) - s * 2, bar: Math.max(intro, drop1 - 2), step: 0, vel: 1 });
  if (drop2 !== undefined && brk !== undefined) {
    const from = brk + sizeOf(form, 'break') / 2;
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2) - s * 2, bar: from, step: 0, vel: 1 });
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
  }
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (groove1 !== undefined) hit('crash', groove1, 0, 0.6);

  return {
    id: trackId(CARIMBO, seed, p),
    template: CARIMBO.id,
    seed,
    params: p,
    title: ch.orig ? CARIMBO.ref.title : titleOf(seed),
    genre: CARIMBO.genre,
    bpm: p.bpm,
    swing: 58,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 1.6, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.2, drive: 1.4, softness: 1.3, pumpDepth: 0.3, pumpRelease: 0.2 },
    events,
    autos,
    accents: { curimbo: 'kick', tapa: 'snare', banjo: 'stab', flute: 'melody', sax: 'melody', impact: 'drop' },
  };
}

export const CARIMBO: Template = {
  id: 'carimbo',
  genre: 'carimbó',
  bpm: [112, 132],
  ref: { seed: 'vitoria-regia', title: 'Vitória-régia', params: { bpm: 120, bars: 28, energy: 0.55 } },
  score,
};
